import * as React from 'react';
import { cn } from '../../lib/cn.js';

export interface ProgressBarProps {
  /** 0–100, or null for an unknown / indeterminate amount. */
  value: number | null;
  /** Accessible name, e.g. "Progreso de configuración". */
  label: string;
  /** Visible text, e.g. "6 de 10 pasos". Defaults to the percentage. */
  valueText?: string;
  tone?: 'brand' | 'success' | 'warning' | 'danger';
  /** Hide the visible value text (still exposed via aria-valuetext). */
  hideValue?: boolean;
  className?: string;
}

const TONES = { brand: 'bg-brand-primary', success: 'bg-success', warning: 'bg-warning', danger: 'bg-danger' } as const;

export const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(function ProgressBar(
  { value, label, valueText, tone = 'brand', hideValue, className },
  ref,
) {
  const pct = value === null ? null : Math.max(0, Math.min(100, Math.round(value)));
  const text = valueText ?? (pct === null ? 'Desconocido' : `${pct}%`);
  return (
    <div ref={ref} className={cn('grid gap-1', className)}>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct ?? undefined}
        aria-valuetext={text}
        className="h-2 w-full overflow-hidden rounded-pill bg-surface-subtle"
      >
        <div
          className={cn('h-full rounded-pill transition-[width] duration-[var(--ef-motion-base)]', TONES[tone], pct === null && 'w-1/3 animate-pulse')}
          style={pct === null ? undefined : { width: `${pct}%` }}
        />
      </div>
      {hideValue ? null : <span className="ef-tabular text-meta text-text-secondary">{text}</span>}
    </div>
  );
});
